<template>
    <validation-observer ref="regForm" slim #default="{ invalid }">
        <form class="registration-form" id="registration_form" @submit.prevent="register(invalid)">
            <div class="registration-form__inner" v-if="!isFormSent || currentUserExistFlow === 'orderFlow'">
                <ul class="registration-form__switch">
                    <li class="registration-form__switch-option"
                        :class="{'active': !isCompany}"
                        @click="isCompany=false"
                    >
                        Физическое лицо
                    </li>
                    <li class="registration-form__switch-option"
                        :class="{'active': isCompany}"
                        @click="isCompany=true"
                    >
                        Юридическое лицо
                    </li>
                </ul>
                <div class="order-form__row row">
                    <div class="col-12" :class="{'col-sm-6': !isPhoneConfirmed}">
                        <validation-provider rules="required|phone" #default="{ errors }">
                            <text-input
                                label="Телефон"
                                name="phone"
                                mask="phone"
                                :class="{
                                        'has-error': isPhoneInvalid || errors.length,
                                        'has-success': isPhoneConfirmed && !errors.length,
                                    }"
                                :status="getPhoneStatusContextual(errors)"
                                :readonly="isPhoneConfirmed"
                                v-model="formModel.phone"
                                @input="onPhoneInput"
                                @keypress.enter.native.prevent="sendCode()"
                                inputmode="numeric"
                            ></text-input>
                        </validation-provider>
                    </div>
                    <div class="col-12 col-sm-6" v-if="!isPhoneConfirmed || sendCodeAvailable">
                        <button
                            type="button"
                            class="btn btn-main btn-block"
                            :disabled="isSendCodeBtnDisabled || invalid || needCaptcha"
                            v-if="!showCodeInput"
                            @click="sendCode()"
                        >
                            <span v-if="isLoading" class="spinner-border spinner-border-sm"/>
                            <template v-else>Получить код</template>
                        </button>
                        <validation-provider rules="required|smsCode" #default="{ errors }">
                            <text-input
                                label="Код из смс"
                                name="smscode"
                                :class="{
                                        'has-error': isCodeInvalid || errors.length,
                                        'has-success': isPhoneConfirmed && !errors.length,
                                    }"
                                :status="codeStatus || errors[0]"
                                v-if="showCodeInput"
                                v-model="formModel.smscode"
                                @input="onCodeInput"
                                @keypress.enter.native.prevent="phoneAuth"
                                inputmode="numeric"
                                ref="code"
                                :autocomplete="'one-time-code'"
                            ></text-input>
                        </validation-provider>
                    </div>

                    <div class="col-12 col-sm-6 registration-form__code-info" v-if="showCodeInput">
                        <p class="registration-form__request-code"
                           v-if="!countdown">
                            <span @click="sendCode()">Запросить код</span>
                        </p>
                        <div class="registration-form__hint"
                             v-if="countdown">
                            <p>Не приходит СМС?</p>
                            <p>Запросить код повторно через <b>{{ countdown }} с.</b></p>
                        </div>
                    </div>

                    <div class="col-12 col-sm-6 ml-auto" v-if="showCodeInput">
                        <button
                            type="button"
                            class="btn btn-main btn-block"
                            :disabled="isConfirmCodeBtnDisabled"
                            @click="phoneAuth"
                        >
                            <span v-if="isLoading" class="spinner-border spinner-border-sm"/>
                            <template v-else>Подтвердить</template>
                        </button>
                    </div>

                    <template v-if="isPhoneConfirmed && isRegistration">
                        <div :class="isCompany ? 'col-12' : 'col-12 col-sm-6'">
                            <validation-provider rules="required|nameRu" #default="{ errors }">
                                <text-input
                                    :class="{
                                        'has-error': errors.length,
                                    }"
                                    :label="'ФИО'"
                                    :status="errors[0]"
                                    name="UF_NAME"
                                    v-model="formModel.UF_NAME"
                                ></text-input>
                            </validation-provider>
                        </div>
                        <template v-if="isCompany">
                            <div class="col-12 col-sm-6">
                                <validation-provider rules="required|email" #default="{ errors }">
                                    <text-input
                                        label="Электронная почта"
                                        name="UF_EMAIL"
                                        :class="{
                                            'has-error': emailStatus || errors.length,
                                            'has-success': isEmailConfirm(errors),
                                            'has-loading': isEmailChecking,
                                        }"
                                        :status="emailStatus || errors[0]"
                                        v-model="formModel.UF_EMAIL"
                                        :hint="isCompany ? 'На неё мы вышлем договор' : ''"
                                    ></text-input>
                                </validation-provider>
                            </div>
                            <div class="col-12 col-sm-6">
                                <validation-provider rules="required|city" #default="{ errors }">
                                    <text-input-suggest
                                        label="Город"
                                        name="UF_CITY"
                                        v-model="formModel.UF_CITY"
                                        :class="{
                                            'has-error': errors.length,
                                        }"
                                        placeholder="Город"
                                        item-value-key="VALUE"
                                        :items="suggestCity"
                                        :status="errors[0]"
                                        @input="getSuggestCity"
                                    ></text-input-suggest>
                                </validation-provider>
                            </div>
                        </template>
                        <template v-else>
                            <div class="col-12 col-sm-6">
                                <validation-provider rules="required|email" #default="{ errors }">
                                    <text-input
                                        label="Электронная почта"
                                        name="UF_EMAIL"
                                        :class="{
                                            'has-error': emailStatus || errors.length,
                                            'has-success': isEmailConfirm(errors),
                                            'has-loading': isEmailChecking,
                                        }"
                                        :status="emailStatus || errors[0]"
                                        v-model="formModel.UF_EMAIL"
                                        :hint="isCompany ? 'На неё мы вышлем договор' : ''"
                                        inputmode="email"
                                    ></text-input>
                                </validation-provider>
                            </div>
                        </template>

                        <template v-if="isCompany">
                            <div class="col-12">
                                <validation-provider rules="required" #default="{ errors }">
                                    <text-input-suggest
                                        label="Название компании"
                                        name="UF_COMPANY"
                                        v-model="formModel.UF_COMPANY"
                                        :class="{
                                            'has-error': errors.length,
                                        }"
                                        placeholder="Начните вводить название компании"
                                        item-value-key="VALUE"
                                        :items="suggestCompanyName"
                                        :status="errors[0]"
                                        @input="getSuggestCompanyName"
                                        @select="completeCompany"
                                    ></text-input-suggest>
                                </validation-provider>
                            </div>
                            <div class="col-12">
                                <validation-provider rules="" #default="{ errors }">
                                    <radio-group
                                        name="UF_VAT_SYSTEM"
                                        label="Система налогообложения"
                                        v-model="formModel.UF_VAT_SYSTEM"
                                        :class="{ 'has-error': errors.length }"
                                        :items="vatItems"
                                        :status="errors[0]"
                                    >
                                    </radio-group>
                                </validation-provider>
                            </div>
                            <div class="col-12 col-sm-6">
                                <validation-provider rules="required|inn" #default="{ errors }">
                                    <text-input-suggest
                                        label="ИНН компании"
                                        name="UF_INN"
                                        v-model="formModel.UF_INN"
                                        :class="{'has-error': errors.length}"
                                        placeholder="ИНН компании"
                                        item-value-key="VALUE"
                                        :items="suggestCompanyINN"
                                        :status="errors[0]"
                                        @input="getSuggestCompanyINN"
                                        @select="completeCompany"
                                    ></text-input-suggest>
                                </validation-provider>
                            </div>
                            <div class="col-12 col-sm-6">
                                <validation-provider rules="kpp" #default="{ errors }">

                                    <text-input-suggest
                                        label="КПП компании"
                                        name="UF_KPP"
                                        v-model="formModel.UF_KPP"
                                        :class="{'has-error': errors.length}"
                                        placeholder="КПП компании"
                                        item-value-key="VALUE"
                                        :items="suggestCompanyKPP"
                                        :status="errors[0]"
                                        @input="getSuggestCompanyKPP"
                                        @select="completeCompany"
                                    ></text-input-suggest>
                                </validation-provider>
                            </div>
                            <div class="col-12">
                                <validation-provider rules="required|address" #default="{ errors }">
                                    <text-input-suggest
                                        :field-type="'textarea'"
                                        label="Юридический адрес"
                                        name="UF_COMPANY_ADR"
                                        placeholder="Юридический адрес"
                                        item-value-key="VALUE"
                                        :class="{'has-error': errors.length}"
                                        :status="errors[0]"
                                        :items="suggestAddress"
                                        v-model="formModel.UF_COMPANY_ADR"
                                        @input="getSuggestAddresses"
                                    ></text-input-suggest>
                                </validation-provider>
                            </div>

                            <div class="col-12">
                                <checkbox-input
                                    label="Адрес доставки совпадает с юридическим адресом"
                                    name="UF_ADDRESSES_EQUAL"
                                    v-model="formModel.UF_ADDRESSES_EQUAL"
                                ></checkbox-input>
                            </div>

                            <div class="col-12" v-if="!formModel.UF_ADDRESSES_EQUAL">
                                <validation-provider vid="UF_ADDRESS" rules="required|address" #default="{ errors }">
                                    <text-input-suggest
                                        :field-type="'textarea'"
                                        label="Фактический адрес"
                                        name="UF_ADDRESS"
                                        placeholder="Фактический адрес"
                                        item-value-key="VALUE"
                                        :class="{'has-error': errors.length}"
                                        :status="errors[0]"
                                        :items="suggestAddress"
                                        v-model="formModel.UF_ADDRESS"
                                        @input="getSuggestAddresses"
                                    ></text-input-suggest>
                                </validation-provider>
                            </div>

                            <div class="col-6">
                                <validation-provider rules="bik" #default="{ errors }">
                                    <text-input-suggest
                                        label="БИК"
                                        name="UF_BIC"
                                        v-model="formModel.UF_BIC"
                                        :class="{ 'has-error': errors.length }"
                                        placeholder="БИК"
                                        item-value-key="VALUE"
                                        :items="suggestBank"
                                        :status="errors[0]"
                                        @input="getSuggestBank"
                                        @select="completeBank"
                                    ></text-input-suggest>
                                </validation-provider>
                            </div>
                            <div class="col-6">
                                <validation-provider rules="city" #default="{ errors }">
                                    <text-input-suggest
                                        label="Город банка"
                                        name="UF_BANK_CITY"
                                        v-model="formModel.UF_BANK_CITY"
                                        :class="{
                                            'has-error': errors.length,
                                        }"
                                        placeholder="Город"
                                        item-value-key="VALUE"
                                        :items="suggestCity"
                                        :status="errors[0]"
                                        @input="getSuggestCity"
                                    ></text-input-suggest>
                                </validation-provider>
                            </div>
                            <div class="col-12">
                                <validation-provider rules="bankName" #default="{ errors }">
                                    <text-input-suggest
                                        label="Название банка"
                                        name="UF_BANK_NAME"
                                        v-model="formModel.UF_BANK_NAME"
                                        :class="{
                                            'has-error': errors.length,
                                        }"
                                        placeholder="Название банка"
                                        item-value-key="VALUE"
                                        :items="suggestBank"
                                        :status="errors[0]"
                                        @input="getSuggestBank"
                                        @select="completeBank"
                                    ></text-input-suggest>
                                </validation-provider>
                            </div>
                            <div class="col-12">
                                <validation-provider rules="accountNumber" #default="{ errors }">

                                    <text-input
                                        label="Расчётный счёт"
                                        name="UF_CHECKING_ACCOUNT"
                                        v-model="formModel.UF_CHECKING_ACCOUNT"
                                        :class="{
                                                'has-error': errors.length,
                                            }"
                                        :status="errors[0]"
                                    ></text-input>
                                </validation-provider>
                            </div>
                            <div class="col-12">
                                <validation-provider rules="accountNumber" #default="{ errors }">
                                    <text-input
                                        label="Кор. счёт"
                                        name="UF_CORR_ACCOUNT"
                                        v-model="formModel.UF_CORR_ACCOUNT"
                                        :class="{
                                                'has-error': errors.length,
                                            }"
                                        :status="errors[0]"
                                    ></text-input>
                                </validation-provider>
                            </div>

                            <div class="col-12">
                                <slot name="offer"></slot>
                            </div>
                        </template>

                        <div class="col-12 col-sm-6 registration-form__terms">
                            <slot name="term"></slot>
                        </div>

                        <div class="col-12 col-sm-6">
                            <button
                                type="submit"
                                class="btn btn-main btn-block"
                                :disabled="invalid || isEmailExist || formStatus"
                            >
                                <span v-if="isLoading" class="spinner-border spinner-border-sm"/>
                                <template v-else>{{ submitButtonText }}</template>
                            </button>
                            <p class="registration-form__status" v-if="formStatus" v-html="formStatus"/>
                        </div>
                    </template>
                </div>

                <div class="order-form__row row margin-captcha-reg">
                    <div id="captcha-container-reg"></div>
                </div>

                <div class="order-form__row" v-if="captchaLoadError" role="alert">
                    <p class="text-danger mb-2">{{ captchaLoadError }}</p>
                    <button type="button" class="btn btn-link p-0" @click="initCaptcha()">Повторить загрузку</button>
                </div>

            </div>

            <slot name="success"></slot>
        </form>
    </validation-observer>
</template>

<script>
import TextInput from '../fields/text-input';
import RadioGroup from '../fields/radio-group';

import { dadataMixin } from '../../../mixins/dadata';
import { smartCaptchaMixin } from '../../../mixins/smart-captcha-mixin';
import { SMART_CAPTCHA_SITEKEY } from '../../../services/smart-captcha';
import { postForm, NO_CONNECTION_TEXT } from '../../../services/form-request';
import { VAT_ITEMS } from '../../../constants';

export default {
    name: 'registration-form',

    components: {
        'text-input': TextInput,
        'radio-group': RadioGroup,
    },

    mixins: [dadataMixin, smartCaptchaMixin],

    props: {
        submitButtonText: {
            type: String,
            default: 'Зарегистрироваться',
        },

        currentUserExistFlow: {
            type: String,
            default: 'regFlow',
        },
    },

    data() {
        return {
            csrf: BX.bitrix_sessid(),
            isMobile: BX.browser.IsMobile(),
            isFormSent: false,

            formModel: {
                UF_ADDRESSES_EQUAL: 'Y',
                UF_COMPANY_ADR: '',
            },

            requestErrors: null,
            formStatus: '',
            phoneStatus: '',
            codeStatus: '',

            isCompany: window.location.hash === '#company',
            isRegistration: false,
            isCodeSent: false,
            isCodeInvalid: false,
            isPhoneInvalid: false,
            isPhoneConfirmed: false,
            phonePrevious: null,
            needCaptcha: false,
            // isSameAddress: false,
            widgetId: null,
            widgetPromise: null,
            isCaptchaInvisible: true,
            isCaptchaTokenChecked: false,

            vatItems: VAT_ITEMS,

            codeTimeout: 60,
            countdown: 0,
            countdownInterval: null,

            isLoading: false,

            isEmailExist: true,
            isEmailChecking: false,
            emailStatus: null,
            emailCheckTimer: null,
            emailCheckTimeout: 1000,

            USER_EXIST_FLOW: {
                regFlow: () => { this.phoneStatus = 'Такой пользователь уже существует'; },
                orderFlow: () => { this.sendCode('user'); },
            },
        };
    },

    computed: {
        isSendCodeBtnDisabled() {
            return !this.formModel.phone || this.isLoading;
        },

        isConfirmCodeBtnDisabled() {
            return !this.formModel.smscode || this.isLoading;
        },

        formData() {
            const formData = new FormData();
            Object.keys(this.formModel).forEach((key) => {
                if (key === 'UF_ADDRESS') {
                    formData.append('UF_ADDRESS[]', this.formModel[key]);
                } else {
                    formData.append(key, this.formModel[key]);
                }
            });
            formData.set('UF_PHONE', this.formModel.phone);
            return formData;
        },

        email() {
            return this.formModel.UF_EMAIL;
        },

        companyAddress() {
            return this.formModel.UF_COMPANY_ADR;
        },

        isAddressesEqual() {
            return this.formModel.UF_ADDRESSES_EQUAL;
        },

        sendCodeAvailable() {
            return this.formModel.phone !== this.phonePrevious;
        },

        isPhoneChange() {
            return !this.isPhoneInvalid && this.phonePrevious && this.isCodeSent && this.formModel.phone !== this.phonePrevious;
        },

        showCodeInput() {
            return (this.isCodeSent && !this.isPhoneConfirmed) && !this.isPhoneChange;
        },
    },

    methods: {
        getPhoneStatusContextual(errors) {
            if (!errors.length && !this.phoneStatus && this.isMobile) {
                return 'Телефон для входа в личный кабинет';
            }

            return this.phoneStatus || errors[0];
        },

        onPhoneInput() {
            this.isPhoneInvalid = false;
            this.phoneStatus = '';
        },

        onCodeInput() {
            this.isCodeInvalid = false;
            this.codeStatus = '';
        },

        /**
         * Обратный отсчет до повторной отправки кода
         */
        resetCountdown() {
            this.countdown = +this.codeTimeout;
            this.countdownInterval = setInterval(() => {
                if (this.countdown > 0) {
                    this.countdown -= 1;
                } else {
                    clearInterval(this.countdownInterval);
                    this.countdownInterval = null;
                    this.phoneStatus = '';
                }
            }, 1000);
        },

        captchaTokenGenerated(token) {
            console.log("New token: " + token);
            this.isCaptchaTokenChecked = false;
            this.phoneStatus = "";
            if (this.isCaptchaInvisible) {
                this.sendSmsCode('register');
            }
        },

        /**
         * captcha.js грузится по требованию (T-17b); при сбое — текст и кнопка «Повторить загрузку».
         * Виджет рисуется один раз; rerender = true — прежняя перерисовка после отправки кода (P-247 не менялось).
         * @return {Promise<boolean>} виджет отрисован
         */
        initCaptcha(rerender = false) {
            if (this.widgetId !== null && !rerender) return Promise.resolve(true);
            if (this.widgetPromise) return this.widgetPromise;

            this.widgetPromise = this.ensureSmartCaptcha()
                .then((smartCaptcha) => {
                    if (!smartCaptcha) return false;

                    this.widgetId = smartCaptcha.render('captcha-container-reg', {
                        sitekey: SMART_CAPTCHA_SITEKEY,
                        invisible: this.isCaptchaInvisible,
                        hideShield: true,
                        callback: (token) => this.captchaTokenGenerated(token),
                    });
                    return true;
                })
                .finally(() => {
                    this.widgetPromise = null;
                });

            return this.widgetPromise;
        },

        sendCode(flow = 'register') {
            if (this.isSendCodeBtnDisabled) {
                return;
            }

            if (this.formModel.phone) {
                this.isLoading = true;
                if (this.widgetId === null) {
                    this.initCaptcha().then((isReady) => {
                        this.isLoading = false;
                        if (isReady) this.sendCode(flow);
                    });
                    return;
                }
                if (this.isCaptchaInvisible) {
                    window.smartCaptcha.execute(this.widgetId);
                } else {
                    if (!this.isCaptchaTokenChecked) {
                        this.sendSmsCode(flow);
                    } else {
                        this.phoneStatus = "Пройдите капчу!";
                        this.isLoading = false;
                    }
                }
            }
        },

        sendSmsCode(flow) {
            this.formData.append('csrf', BX.bitrix_sessid());
            this.formData.append('smart-token', window.smartCaptcha.getResponse(this.widgetId));
            postForm(`/api/${flow}/send-code/`, this.formData)
                .then((result) => {
                    const isSuccess = result.status === 'success';
                    this.isLoading = false;
                    this.isCodeSent = isSuccess;
                    this.isPhoneInvalid = !isSuccess;
                    this.phonePrevious = this.formModel.phone;
                    this.isCaptchaInvisible = false;
                    this.isCaptchaTokenChecked = true;

                    if (result.message === 'phone_exists') {
                        if (flow === 'register') {
                            this.isCaptchaTokenChecked = false;
                        }
                        this.USER_EXIST_FLOW[this.currentUserExistFlow]();
                        this.initCaptcha(true);
                        return;
                    }
                    this.initCaptcha(true);

                    this.phoneStatus = isSuccess ? 'Код подтверждения отправлен' : result.message;

                    if (isSuccess) {
                        clearInterval(this.countdownInterval);
                        this.resetCountdown();
                    }
                })
                .catch(() => {
                    this.isLoading = false;
                    this.phoneStatus = NO_CONNECTION_TEXT;
                });
        },

        phoneAuth() {
            if (this.formModel.phone && this.formModel.smscode) {
                this.isLoading = true;

                fetch('/api/user/phone-auth/', {
                    method: 'POST',
                    body: this.formData,
                })
                    .then((response) => response.json())
                    .then((result) => {
                        const isSuccess = result.status === 'success';
                        this.isCodeInvalid = !isSuccess;

                        if (isSuccess) {
                            // Успешно вошли - перезагрузить страницу, чтобы обновить данные пользователя
                            window.location.reload();
                            return;
                        }

                        this.isLoading = false;

                        if (result.message === 'Пользователь не найден') {
                            // Пользователь не найден - включить режим регистрации
                            this.isRegistration = true;
                            this.confirmCode();
                        } else {
                            // Пользователь существует, но код введён неверно
                            this.codeStatus = result.message;
                        }
                    });
            }
        },

        confirmCode() {
            if (this.formModel.phone && this.formModel.smscode) {
                this.isLoading = true;

                const formData = new FormData();
                formData.append('phone', this.formModel.phone);
                formData.append('smscode', this.formModel.smscode);

                fetch('/api/user/confirm-code/', {
                    method: 'POST',
                    body: formData,
                })
                    .then((response) => response.json())
                    .then((result) => {
                        const isSuccess = result.status === 'success';
                        this.isLoading = false;
                        this.isCodeInvalid = !isSuccess;
                        this.isPhoneConfirmed = isSuccess;
                        this.codeStatus = isSuccess ? 'Вход подтверждён' : result.message;
                        this.phoneStatus = isSuccess ? 'Телефон подтверждён' : '';

                        if (isSuccess) {
                            clearInterval(this.countdownInterval);
                            this.countdownInterval = null;
                        }
                    });
            }
        },

        registerErrorHandler(result) {
            this.isLoading = false;
            this.formStatus = 'Ошибка. Проверьте корректность вводимых данных.';

            if (!result) {
                throw new Error('Неверный формат данных');
            }

            this.requestErrors = result?.data?.FIELDS;

            if (this.requestErrors) {
                this.$refs.regForm.setErrors(this.requestErrors); // функция валидатора
                return;
            }

            if (result.message) {
                this.formStatus = result.message;
            }
        },

        register(invalid) {
            if (invalid) return;

            const url = `/api/user/register-${this.isCompany ? 'ur' : 'phiz'}/`;
            const formDataReg = this.formData;
            formDataReg.delete('phone');
            formDataReg.delete('smscode');
            if (this.formData.get('UF_ADDRESSES_EQUAL') === 'false') this.formData.delete('UF_ADDRESSES_EQUAL');
            this.isLoading = true;

            fetch(url, {
                method: 'POST',
                body: formDataReg,
            })
                .then((response) => response.json())
                .then((result) => {
                    const isSuccess = result.status === 'success';
                    const isError = result.status === 'error';

                    if (isError) {
                        this.registerErrorHandler(result);
                        return;
                    }

                    if (isSuccess) {
                        this.isSuccess = '';
                        this.isFormSent = true;
                        this.$emit('success');
                        return;
                    }

                    this.formStatus = result.message;

                    throw Error('Ошибка авторизации');
                })
                .catch(this.registerErrorHandler);
        },

        /**
         * Проверка наличия в базе уже зарегистрированного email
         */
        checkEmail() {
            const url = '/api/register/email/';

            const formData = new FormData();
            formData.append('email', this.email);

            this.isEmailChecking = true;

            fetch(url, {
                method: 'POST',
                body: formData,
            })
                .then((response) => response.json())
                .then((result) => {
                    if (result.message === 'email_exists') {
                        this.isEmailExist = true;
                        this.emailStatus = 'Пользователь с таким email уже зарегистрирован';
                    } else {
                        this.isEmailExist = false;
                    }

                    this.isEmailChecking = false;
                });
        },

        isEmailConfirm(validationErrors) {
            return this.formModel.UF_EMAIL && !validationErrors.length && !this.isEmailChecking && !this.isEmailExist;
        },
    },

    mounted: function() {
        this.initCaptcha();
    },

    watch: {
        formModel: {
            immediate: true,
            deep: true,
            handler() {
                this.formStatus = null; // сброс ошибки формы
            },
        },

        isCodeSent(value) {
            if (value) {
                this.$nextTick(() => {
                    try {
                        this.$refs.code.focus();
                    } catch (e) {
                        //
                    }
                });
            }
        },

        email(value) {
            clearTimeout(this.emailCheckTimer);

            if (value) {
                this.emailStatus = null;
                this.emailCheckTimer = setTimeout(this.checkEmail, this.emailCheckTimeout);
            }
        },

        // Если адрес компании изменился, и чекбокс на совпадение адресов стоит,
        // то ставим значение фактического адреса из адреса компании
        companyAddress(value) {
            if (value && this.formModel.UF_ADDRESSES_EQUAL) {
                this.formModel.UF_ADDRESS = value;
            }
        },

        // Если чекбокс на совпадение адресов стоит,
        // то ставим значение фактического адреса из адреса компании
        isAddressesEqual() {
            if (this.formModel.UF_ADDRESSES_EQUAL) {
                this.formModel.UF_ADDRESS = this.formModel.UF_COMPANY_ADR;
            } else {
                this.formModel.UF_ADDRESS = '';
            }
        },
    },
};
</script>
